import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { translations, LANG_OPTIONS, uiLangFor } from '../i18n';

// Re-export the language options so call sites that want a dropdown can
// `import { LANG_OPTIONS } from '../contexts/I18nContext'`.
export { LANG_OPTIONS };

const I18nContext = createContext(null);

// Detect the best supported language from navigator preferences on first
// visit. Walks `navigator.languages` in order (most-preferred first) and
// returns the first 2-letter prefix that maps to one of our translation
// bundles. Falls back to 'en' when nothing matches or in non-browser envs
// (SSR / tests).
function detectBrowserLang() {
  if (typeof navigator === 'undefined') return 'en';
  const prefs = Array.isArray(navigator.languages) && navigator.languages.length
    ? navigator.languages
    : [navigator.language || ''];
  for (const raw of prefs) {
    const code = String(raw).slice(0, 2).toLowerCase();
    if (translations[code]) return code;
  }
  return 'en';
}

// Module-level flag — survives React.StrictMode's mount → unmount → remount
// cycle so the auto-detect toast still fires even when the dev StrictMode
// pass cancels the first scheduled setTimeout. The flag is set inside the
// useState initializer (which runs in every StrictMode pass while
// localStorage is still empty) and cleared only AFTER the toast actually
// renders.
let pendingAutoDetect = null;

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem('e2care.lang');
    if (saved && translations[saved]) return saved;
    const detected = detectBrowserLang();
    // Only notify when detection picked something other than 'en'. On EN
    // browsers (or unsupported locales that fell back to EN), there's
    // nothing interesting to surface.
    if (detected !== 'en') pendingAutoDetect = detected;
    return detected;
  });

  useEffect(() => {
    localStorage.setItem('e2care.lang', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key, vars) => {
    const ui = uiLangFor(lang);
    // Unknown backend event types fall back to a readable label, never a raw key.
    const raw = translations[ui]?.[key] ?? translations.en[key]
      ?? (key.startsWith('event_') ? key.slice(6).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : key);
    if (!vars || typeof raw !== 'string') return raw;
    return raw.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
  };

  // One-shot auto-detection toast. Uses a session-level localStorage flag
  // so the message appears at most once per browser, not every page load.
  useEffect(() => {
    const code = pendingAutoDetect;
    if (!code) return;

    if (localStorage.getItem('e2care.lang.autodetect.notified') === '1') {
      pendingAutoDetect = null;
      return;
    }

    const label = LANG_OPTIONS.find((o) => o.code === code)?.label || code.toUpperCase();
    // Small delay so the toast doesn't paint during React's first commit;
    // also gives the user time to register the page before a notification
    // pops in.
    const id = setTimeout(() => {
      toast.info(t('lang_autodetected_toast', { language: label }), { duration: 6000 });
      localStorage.setItem('e2care.lang.autodetect.notified', '1');
      pendingAutoDetect = null;
    }, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
